import React, { useState } from 'react';
import { Dialog, DialogTitle, DialogContent } from '@mui/material';
import { useDispatch, useSelector } from 'react-redux';
import { AppDispatch } from '@/setup/redux/Store';
import AppForm, { AppFormSection } from '@/__core/components/AppForm';
import { createCustomer } from '../redux/CustomerRedux';
import { useCurrentUserCompany } from '../hooks/useCurrentUserCompany';
import LoadingBackdrop from '@/__core/helpers/components/LoadingBackdrop';
import { MessageSnackbar } from '@/__core/helpers/components';
import AddressField from '@/modules/addresses/components/AddressField';

interface CustomerCreationDialogProps {
  open: boolean;
  onClose: () => void;
  onSave: (customerData: any) => Promise<any>;
  loading?: boolean;
  companyId?: string;
}

interface CustomerFormData {
  name: string;
  code: string;
  email: string;
  phone: string;
  address: {
    address_line: string;
    province: string;
    ward: string;
    free_address: string;
  };
  customer_type: string;
  status: string;
  priority: string;
  tax_code: string;
  website: string;
  credit_limit: string;
  payment_terms: string;
  contact_person: string;
  contact_phone: string;
  contact_email: string;
  notes: string;
}

export default function CustomerCreationDialog({
  open,
  onClose,
  onSave,
  loading = false,
  companyId,
}: CustomerCreationDialogProps) {
  const dispatch = useDispatch<AppDispatch>();
  const { getPrimaryCompany } = useCurrentUserCompany();
  
  // Fallback selected company from auth state
  const selectedCompany = useSelector((state: any) => state.auth.selectedCompany);
  
  // Get customer state from Redux
  const customerState = useSelector((state: any) => state.customer);
  const { loading: reduxLoading, error: reduxError } = customerState || {};
  
  const [formData, setFormData] = useState<CustomerFormData>({
    name: '',
    code: '',
    email: '',
    phone: '',
    address: {
      address_line: '',
      province: '',
      ward: '',
      free_address: '',
    },
    customer_type: 'individual',
    status: 'active',
    priority: 'medium',
    tax_code: '',
    website: '',
    credit_limit: '',
    payment_terms: '',
    contact_person: '',
    contact_phone: '',
    contact_email: '',
    notes: '',
  });

  const [errors, setErrors] = useState<{[key: string]: string}>({});
  const [successMessage, setSuccessMessage] = useState<string | null>(null);
  const [errorMessage, setErrorMessage] = useState<string | null>(null);

  const normalizeCompanyId = React.useCallback((value?: string | null) => {
    const normalized = (value || '').trim();
    if (!normalized || normalized === 'all' || normalized === 'global') return '';
    return normalized;
  }, []);

  const resolvedCompanyId = React.useMemo(() => {
    const fromDialogProp = normalizeCompanyId(companyId);
    if (fromDialogProp) return fromDialogProp;

    const fromStoredPrimary = normalizeCompanyId(getPrimaryCompany() || '');
    if (fromStoredPrimary) return fromStoredPrimary;

    return normalizeCompanyId(selectedCompany?.company_id || '');
  }, [companyId, getPrimaryCompany, normalizeCompanyId, selectedCompany?.company_id]);

  const handleInputChange = (field: string, value: string | string[] | any) => {
    // Handle address object separately
    if (field === "address") {
      setFormData(prev => ({
        ...prev,
        address: value,
      }));
      return;
    }

    setFormData(prev => ({
      ...prev,
      [field]: value
    }));
    
    // Clear error when user starts typing
    if (errors[field]) {
      setErrors(prev => ({
        ...prev,
        [field]: ''
      }));
    }
  };

  // Check if form is valid
  const isFormValid = React.useMemo(() => {
    return formData.name.trim() !== '' && formData.phone.trim() !== '';
  }, [formData.name, formData.phone]);

  const getFieldError = React.useCallback((field: string) => {
    return errors[field] || undefined;
  }, [errors]);

  const validateForm = () => {
    const newErrors: {[key: string]: string} = {};

    // Required fields
    if (!formData.name.trim()) {
      newErrors.name = 'Tên khách hàng là bắt buộc';
    }

    if (!formData.phone.trim()) {
      newErrors.phone = 'Số điện thoại là bắt buộc';
    }

    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleSave = async () => {
    // Clear previous messages
    setSuccessMessage(null);
    setErrorMessage(null);
    
    const isValid = validateForm();
    
    if (isValid) {
      try {
        if (!resolvedCompanyId) {
          setErrorMessage('Không xác định được công ty đang chọn để tạo khách hàng');
          return;
        }

        // Prepare data for submission - map to CreateCustomerRequest format
        const { address } = formData;
        const submitData = {
          name: formData.name,
          email: formData.email || undefined,
          phone: formData.phone || undefined,
          customer_type: formData.customer_type as 'individual' | 'company' | 'government' | 'non_profit',
          status: formData.status as 'active' | 'inactive' | 'pending' | 'blocked',
          priority: formData.priority as 'low' | 'medium' | 'high' | 'vip',
          tax_code: formData.tax_code || undefined,
          website: formData.website || undefined,
          credit_limit: formData.credit_limit.trim() ? parseFloat(formData.credit_limit) : undefined,
          payment_terms: formData.payment_terms || undefined,
          contact_person: formData.contact_person || undefined,
          contact_phone: formData.contact_phone || undefined,
          contact_email: formData.contact_email || undefined,
          notes: formData.notes || undefined,
          address_obj: address || undefined,
          company_ids: resolvedCompanyId ? [resolvedCompanyId] : undefined,
        };

        // Use Redux to create customer
        const result = await dispatch(createCustomer(submitData)).unwrap();
        
        // Call onSave callback if provided
        if (onSave) {
          await onSave(result);
        }
        
        setSuccessMessage('Khách hàng đã được tạo thành công!');
        
        // Close dialog after a short delay to show success message
        setTimeout(() => {
          handleClose();
        }, 1500);
      } catch (error) {
        console.error('Error creating customer:', error);
        setErrorMessage('Có lỗi xảy ra khi tạo khách hàng');
      }
    } else {
      setErrorMessage('Vui lòng kiểm tra lại thông tin đã nhập');
    }
  };

  const handleClose = () => {
    setFormData({
      name: '',
      code: '',
      email: '',
      phone: '',
      address: {
        address_line: '',
        province: '',
        ward: '',
        free_address: '',
      },
      customer_type: 'individual',
      status: 'active',
      priority: 'medium',
      tax_code: '',
      website: '',
      credit_limit: '',
      payment_terms: '',
      contact_person: '',
      contact_phone: '',
      contact_email: '',
      notes: '',
    });
    setErrors({});
    setSuccessMessage(null);
    setErrorMessage(null);
    onClose();
  };

  const sections = React.useMemo<AppFormSection[]>(() => {
    const baseSections: AppFormSection[] = [
      {
        key: 'customer-basic',
        title: 'Thông tin cơ bản',
        fields: [
          {
            key: 'name',
            label: formData.customer_type === 'company' ? 'Tên công ty' : 'Tên khách hàng',
            type: 'text',
            labelWidth: 100,
            required: true,
            error: !!getFieldError('name'),
            helperText: getFieldError('name'),
            gridSize: { xs: 12, md: 6 },
          },
          {
            key: 'email',
            label: formData.customer_type === 'company' ? 'Email công ty' : 'Email',
            labelWidth: 100,
            type: 'text',
            error: !!getFieldError('email'),
            helperText: getFieldError('email'),
            gridSize: { xs: 12, md: 6 },
          },
          {
            key: 'phone',
            label: formData.customer_type === 'company' ? 'SĐT công ty' : 'Số điện thoại',
            labelWidth: 100,
            type: 'text',
            required: true,
            error: !!getFieldError('phone'),
            helperText: getFieldError('phone'),
            gridSize: { xs: 12, md: 6 },
          },
          {
            key: 'customer_type',
            label: 'Loại khách hàng',
            labelWidth: 100,
            type: 'select',
            options: [
              { value: 'individual', label: 'Cá nhân' },
              { value: 'company', label: 'Công ty' },
            ],
            gridSize: { xs: 12, md: 6 },
          },
        ],
      },
    ];

    if (formData.customer_type !== 'individual') {
      baseSections.push({
        key: 'company-info',
        title: 'Thông tin công ty',
        fields: [
          {
            key: 'tax_code',
            label: 'Mã số thuế',
            type: 'text',
            gridSize: { xs: 12, md: 6 },
          },
          {
            key: 'website',
            label: 'Website',
            type: 'text',
            gridSize: { xs: 12, md: 6 },
          },
          {
            key: 'credit_limit',
            label: 'Hạn mức tín dụng',
            type: 'text',
            gridSize: { xs: 12, md: 6 },
          },
          {
            key: 'payment_terms',
            label: 'Điều khoản thanh toán',
            type: 'text',
            gridSize: { xs: 12, md: 6 },
          },
        ],
      });
    }

    if (formData.customer_type === 'company') {
      baseSections.push({
        key: 'contact-representative',
        title: 'Người đại diện liên hệ',
        fields: [
          {
            key: 'contact_person',
            label: 'Họ tên người đại diện',
            type: 'text',
            gridSize: { xs: 12, md: 6 },
          },
          {
            key: 'contact_phone',
            label: 'SĐT người đại diện',
            type: 'text',
            gridSize: { xs: 12, md: 6 },
          },
          {
            key: 'contact_email',
            label: 'Email người đại diện',
            type: 'text',
            gridSize: { xs: 12, md: 6 },
          },
        ],
      });
    }

    baseSections.push({
      key: 'customer-address',
      title: 'Địa chỉ và ghi chú',
      fields: [
        {
          key: 'address',
          label: '',
          type: 'custom',
          standalone: true,
          gridSize: { xs: 12 },
          render: ({ values, onChange }) => (
            <AddressField
              value={values.address}
              onChange={(value) => onChange('address', value)}
              isEditMode={true}
              labelWidth={100}
              styleDisplay='small'
            />
          ),
        },
        {
          key: 'notes',
          label: 'Ghi chú',
          type: 'text',
          rows: 2,
          multiline: true,
          gridSize: { xs: 12 },
        },
      ],
    });

    return baseSections;
  }, [formData.customer_type, getFieldError]);

  return (
    <Dialog 
      open={open} 
      onClose={handleClose}
      maxWidth="md"
      fullWidth
      PaperProps={{
        sx: {
          borderRadius: '12px',
          boxShadow: '0 20px 25px -5px rgba(0, 0, 0, 0.1), 0 10px 10px -5px rgba(0, 0, 0, 0.04)',
        },
      }}
    >
      <DialogTitle sx={{ 
        borderBottom: '1px solid #e5e7eb',
        pb: 1,
        '& .MuiTypography-root': {
          fontSize: '1.25rem',
          fontWeight: 600,
          color: '#111827',
        },
        '& + .MuiDialogContent-root': {
          pt: 2,
        },
      }}>
        Thêm khách hàng mới
      </DialogTitle>

      <DialogContent sx={{ mt:2, px: 2, pb: 2, overflow: 'auto' }}>
        <AppForm
          sections={sections}
          values={formData as Record<string, any>}
          onChange={handleInputChange}
          onSubmit={handleSave}
          onCancel={handleClose}
          submitting={reduxLoading || loading}
          submitDisabled={reduxLoading || loading || !isFormValid}
          submitLabel="Tạo khách hàng"
          submittingLabel="Đang tạo..."
          cancelLabel="Hủy"
          containerSx={{ p: 0, m: 0, width: '100%', backgroundColor: 'transparent' }}
        />
      </DialogContent>
      
      {/* Loading Backdrop */}
      <LoadingBackdrop 
        open={reduxLoading || loading} 
        message="Đang tạo khách hàng..." 
      />

      {/* Success Snackbar */}
      <MessageSnackbar
        open={!!successMessage && !reduxError && !errorMessage}
        message={successMessage}
        severity="success"
        onClose={() => setSuccessMessage(null)}
      />

      {/* Error Snackbar */}
      <MessageSnackbar
        open={!!reduxError || !!errorMessage}
        message={reduxError || errorMessage}
        severity="error"
        onClose={() => {
          setErrorMessage(null);
        }}
      />
    </Dialog>
  );
}
